import { Link, NavLink, useLocation } from 'react-router-dom';
import type { NavItem } from './nav';
import { NavIcon } from './NavIcon';
import { useCommonT, LanguageSwitcher } from '../i18n';

/** Whether `pathname` IS this nav item's own page (not a sub-page of the section). */
function isItemPage(pathname: string, it: NavItem): boolean {
  return pathname === it.to;
}

/**
 * Navy sidebar (JP-CRM Area 11 §2.4): wordmark, nav grouped as the operation
 * works, queue badges, user + language toggle at the bottom.
 * - Items are real <NavLink>s; the active item is derived from the route.
 * - A group label is rendered whenever `group` changes between consecutive items,
 *   so role-filtered navs never show an empty group.
 * - `badge` is optional per item (live counts can be wired in later by screens).
 */
export interface CleanSidebarProps {
  items: readonly NavItem[];
  /** Portal label beside the wordmark, e.g. "Iberia" / "Agent portal". */
  role: string;
  /** Display name of the signed-in user. */
  user: string;
  /** Sub-label under the user name (e.g. office / role). */
  userSubtitle?: string;
  /** Optional per-item badge counts keyed by NavItem.id. */
  badges?: Readonly<Record<string, number | undefined>>;
  /** Footer slot (e.g. a sign-out button). */
  footer?: React.ReactNode;
  /** Called after a nav item / the brand is tapped (used to close the mobile drawer). */
  onNavigate?: () => void;
}

function initials(name: string): string {
  return name
    .split(' ')
    .map((s) => s[0])
    .filter(Boolean)
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

export function CleanSidebar({
  items,
  role,
  user,
  userSubtitle,
  badges,
  footer,
  onNavigate,
}: CleanSidebarProps) {
  const t = useCommonT();
  const location = useLocation();
  // The first nav item is the portal home — the brand block links there.
  const homeTo = items[0]?.to ?? '/';
  return (
    <div className="sidebar">
      <Link to={homeTo} className="sidebar-brand" aria-label={t('go_home')} onClick={onNavigate}>
        <img src="/brand/lockup-on-navy.svg" alt="Jalupro Iberia" />
        {role && <span>{role}</span>}
      </Link>

      <nav className="col" style={{ padding: '4px 10px 10px', flex: 1, overflowY: 'auto' }}>
        {items.map((it, idx) => {
          const badge = badges?.[it.id];
          const showGroup = idx === 0 || items[idx - 1].group !== it.group;
          return (
            <div key={it.id} className="col">
              {showGroup && <div className="nav-group">{t(it.group)}</div>}
              <NavLink
                to={it.to}
                end={it.end}
                className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                onClick={(e) => {
                  onNavigate?.();
                  // Clicking the item for the EXACT page you're on refreshes it (re-fetch
                  // data). From a sub-page (e.g. /clients/:id/edit) the same click is a
                  // normal navigation back to the section root instead.
                  if (isItemPage(location.pathname, it)) {
                    e.preventDefault();
                    window.location.reload();
                  }
                }}
              >
                <span className="icon"><NavIcon name={it.icon} /></span>
                <span style={{ flex: 1 }}>{t(it.labelKey)}</span>
                {badge != null && badge > 0 && <span className="nav-badge">{badge}</span>}
              </NavLink>
            </div>
          );
        })}
      </nav>

      <div style={{ padding: '12px 14px 14px', borderTop: '1px solid var(--side-line)' }}>
        <div className="row center gap-8">
          <div className="avatar">{initials(user)}</div>
          <div className="col" style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 500, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {user}
            </div>
            {userSubtitle && (
              <div style={{ fontSize: 11, color: 'var(--side-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {userSubtitle}
              </div>
            )}
          </div>
          {footer}
        </div>
        <div className="row center between" style={{ marginTop: 10 }}>
          <LanguageSwitcher tone="dark" />
        </div>
      </div>
    </div>
  );
}
